/**
 * *Spis k dátumu* (PRD-0075 R-D4, ADR-0094): the file as the case had learned it by the end of a
 * chosen day. The day is a calendar day in the reader's time zone (`YYYY-MM-DD`, an
 * `<input type="date">` value); the reads are as of the last moment of that day, so a source
 * received that day (stored as its midnight in UTC) is in. Pure: no IO, safe for pure components.
 */
import { dateTimeFormat } from '@/components/humint/format'

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/
const LONG_DAY = { day: 'numeric', month: 'long', year: 'numeric' } as const
const pad2 = (value: number) => String(value).padStart(2, '0')

/** The day as a local date, or null for anything that is not a day of the calendar. */
function localDate(day: string): Date | null {
  const match = DAY.exec(day)
  if (!match) return null
  const [year, month, date] = match.slice(1).map(Number)
  const local = new Date(year, month - 1, date)
  const exact =
    local.getFullYear() === year && local.getMonth() === month - 1 && local.getDate() === date
  return exact ? local : null
}

/** `value` when it is a day of the calendar (`YYYY-MM-DD`), else null. */
export function asOfDay(value: string | null | undefined): string | null {
  return value && localDate(value) ? value : null
}

/** The instant a read is as of: the end of `day` in the reader's zone, as ISO 8601; null for no day. */
export function asOfInstant(day: string | null): string | null {
  const local = day ? localDate(day) : null
  if (!local) return null
  local.setHours(23, 59, 59, 999)
  return local.toISOString()
}

/** Today as a day value: the latest day the file can be read as of. */
export function todayDay(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`
}

/** "1 August 2026" / "1. srpna 2026" for a day value. */
export function formatAsOfDay(day: string, locale: string): string {
  const local = localDate(day)
  return local ? dateTimeFormat(locale, LONG_DAY).format(local) : day
}
